import React, { useState, useEffect, useRef } from 'react';
import { memoize } from 'lodash';
import { APP_EVENTS } from 'Components/utils/responsive/Events.constants';
import { getCurrentBreakpoint } from './Responsive.helper';
import { subscribe, unsubscribe } from './Responsive.listener';
import { ResponsiveContext } from './ResponsiveContext';
import getCurrentResponsive from './getCurrentResponsive';
// prevent rerender of all component using ResponsiveContext when the breakpoint doesn't change
const memoizedGetCurrentResponsive = memoize(getCurrentResponsive);
type Device = 'MOBILE' | 'TABLET' | 'DESKTOP' | void;
type Props = {
  children: React.ReactNode;
  userAgent?: string | void;
  device?: Device;
};

function sendEvent(name) {
  let event;

  if (typeof Event === 'function') {
    event = new Event(name);
  } else {
    event = document.createEvent('Event');
    event.initEvent(name, true, true);
  }

  document.dispatchEvent(event);
}

export function ResponsiveLayout({ children, device }: Props) {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>(() =>
    getCurrentBreakpoint(device),
  );
  const [isAppMounted, setIsAppMounted] = useState(false);

  // Fire mounting event synchronously during first render (mirrors constructor behavior)
  const hasFiredMountingRef = useRef(false);
  if (!hasFiredMountingRef.current) {
    hasFiredMountingRef.current = true;
    if (typeof window !== 'undefined' && !window.cmAppMounted) {
      sendEvent(APP_EVENTS.MOUNTING);
    }
  }

  useEffect(() => {
    // The listener calls componentLike.setState((state) => ...)
    // We need an adapter object that translates this into functional state updates
    const componentLike = {
      setState(
        updater: (state: {
          currentBreakpoint: Breakpoint;
        }) => { currentBreakpoint: Breakpoint } | null,
      ) {
        setCurrentBreakpoint((prev) => {
          const result = updater({ currentBreakpoint: prev });
          if (result === null) {
            return prev;
          }
          return result.currentBreakpoint;
        });
      },
    };

    subscribe(componentLike);

    setCurrentBreakpoint(getCurrentBreakpoint(device));
    setIsAppMounted(true);

    if (!window.cmAppMounted) {
      // Dispatch the info that the app is mounted
      window.cmAppMounted = true;
      sendEvent(APP_EVENTS.MOUNTED);
    }

    return () => {
      unsubscribe(componentLike);
    };
  }, []);

  const value = memoizedGetCurrentResponsive({
    currentBreakpoint,
    isAppMounted,
  });

  return <ResponsiveContext.Provider value={value}>{children}</ResponsiveContext.Provider>;
}
